import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Select } from 'antd';
import type { InputNode } from '../types';
import { RequiredMark } from './RequiredMark';
import { RTE_SELECT_PLACEMENTS, rteSelectPopupContainer } from '../utils/selectPlacements';

type Props = {
  node: InputNode;
  value: string | undefined;
  onChange: (key: string, value: string) => void;
  disabled?: boolean;
  error?: string;
};

type Opt = { label: string; value: string; isCustom?: boolean; title?: string };

/**
 * V1 parity (RichTextEditor/nodes/searchable-select/SearchableSelectPortal.tsx):
 * - 自定义输入：搜索无匹配时回车新增 custom option
 * - 动态宽度：canvas 测当前 label 实际宽度（max 240px）
 * - 紫色 suffixIcon、popupClassName、popupMatchSelectWidth=false；浮层容器由外层 ConfigProvider 统一收口
 * - notFoundContent 自定义、allowClear=!required
 */
export const SearchableSelectNode: React.FC<Props> = React.memo(
  ({ node, value, onChange, disabled, error }) => {
    const placeholder = node.placeholder || '搜索或输入';
    const baseOptions = useMemo<Opt[]>(
      () => (node.options as Opt[] | undefined) ?? [],
      [node.options],
    );

    const effective = value ?? node.defaultValue;

    // 派生 customValue：props.value 不在 baseOptions 中视为自定义
    const customValue = useMemo<string | null>(() => {
      if (!effective) return null;
      return baseOptions.some((o) => String(o.value) === String(effective))
        ? null
        : String(effective);
    }, [effective, baseOptions]);

    const mergedOptions = useMemo<Opt[]>(() => {
      const opts = [...baseOptions];
      if (customValue && !baseOptions.some((o) => String(o.value) === customValue)) {
        opts.unshift({ label: customValue, value: customValue, title: '' });
      }
      return opts.map((option) => ({ ...option, title: '' }));
    }, [baseOptions, customValue]);

    const getEditableText = useCallback(
      (selectedValue: string) => {
        const matched = mergedOptions.find(
          (option) => String(option.value) === String(selectedValue),
        );
        return matched ? matched.label : selectedValue;
      },
      [mergedOptions],
    );

    const [searchText, setSearchText] = useState('');
    const [selectInstanceKey, setSelectInstanceKey] = useState(0);
    const [width, setWidth] = useState<number>(80);
    const wrapperRef = useRef<HTMLSpanElement | null>(null);
    const editingSelectedValueRef = useRef(false);
    const initialEditingTextRef = useRef('');
    const focusEditingRafRef = useRef<number | null>(null);
    const blurEditingRafRef = useRef<number | null>(null);
    const clearFocusResetRafRef = useRef<number | null>(null);
    const preserveEditOnNextBlurRef = useRef(false);
    const ignoreSelectionSearchClearRef = useRef(false);
    const ignoreFocusAfterClearRef = useRef(false);
    const changedValueInCurrentOpenRef = useRef(false);

    const cancelPendingEditFocus = useCallback(() => {
      if (focusEditingRafRef.current === null) return;
      window.cancelAnimationFrame(focusEditingRafRef.current);
      focusEditingRafRef.current = null;
    }, []);

    const cancelPendingEditBlur = useCallback(() => {
      if (blurEditingRafRef.current === null) return;
      window.cancelAnimationFrame(blurEditingRafRef.current);
      blurEditingRafRef.current = null;
    }, []);

    const cancelPendingClearFocusReset = useCallback(() => {
      if (clearFocusResetRafRef.current === null) return;
      window.cancelAnimationFrame(clearFocusResetRafRef.current);
      clearFocusResetRafRef.current = null;
    }, []);

    const startEditingValue = useCallback(
      (text: string, force = false) => {
        cancelPendingEditBlur();
        if (editingSelectedValueRef.current && !force) return;
        cancelPendingEditFocus();
        editingSelectedValueRef.current = true;
        initialEditingTextRef.current = text;
        setSearchText(text);
        focusEditingRafRef.current = window.requestAnimationFrame(() => {
          focusEditingRafRef.current = null;
          const input = wrapperRef.current?.querySelector<HTMLInputElement>(
            '.ant-select-selection-search-input',
          );
          if (!input || input.value !== text) return;
          input.focus();
          input.setSelectionRange(text.length, text.length);
          preserveEditOnNextBlurRef.current = false;
          ignoreSelectionSearchClearRef.current = false;
        });
      },
      [cancelPendingEditBlur, cancelPendingEditFocus],
    );

    const handleBlur = useCallback(() => {
      if (preserveEditOnNextBlurRef.current) {
        preserveEditOnNextBlurRef.current = false;
        return;
      }
      cancelPendingEditBlur();
      blurEditingRafRef.current = window.requestAnimationFrame(() => {
        blurEditingRafRef.current = null;
        editingSelectedValueRef.current = false;
        initialEditingTextRef.current = '';
        setSearchText('');
      });
    }, [cancelPendingEditBlur, effective, node.key, searchText, value]);

    useEffect(
      () => () => {
        cancelPendingEditFocus();
        cancelPendingEditBlur();
        cancelPendingClearFocusReset();
      },
      [cancelPendingClearFocusReset, cancelPendingEditBlur, cancelPendingEditFocus],
    );

    const filteredOptions = useMemo<Opt[]>(() => {
      const keyword = searchText.trim();
      const isUnchangedSelectedText =
        editingSelectedValueRef.current && searchText === initialEditingTextRef.current;
      if (!keyword || isUnchangedSelectedText) return mergedOptions;
      const kw = keyword.toLowerCase();
      const matched = mergedOptions.filter(
        (o) =>
          o.label.toLowerCase().includes(kw) ||
          String(o.value).toLowerCase().includes(kw),
      );
      const exact = mergedOptions.some(
        (o) => o.label === keyword || String(o.value) === keyword,
      );
      if (!exact) {
        return [
          { label: keyword, value: keyword, isCustom: true, title: '' },
          ...matched.map((option) => ({ ...option, title: '' })),
        ];
      }
      return matched.map((option) => ({ ...option, title: '' }));
    }, [mergedOptions, searchText]);

    const handleChange = useCallback(
      (v: string | undefined) => {
        if (v === undefined || v === null) return;
        preserveEditOnNextBlurRef.current = true;
        ignoreSelectionSearchClearRef.current = true;
        changedValueInCurrentOpenRef.current = true;
        startEditingValue(getEditableText(v), true);
        onChange(node.key!, v);
      },
      [getEditableText, node.key, onChange, startEditingValue],
    );

    const handleSearch = useCallback(
      (text: string) => {
        if (!text && ignoreSelectionSearchClearRef.current) {
          ignoreSelectionSearchClearRef.current = false;
          return;
        }
        ignoreSelectionSearchClearRef.current = false;
        cancelPendingEditFocus();
        setSearchText(text);
        if (!editingSelectedValueRef.current) {
          if (!text) return;
          editingSelectedValueRef.current = true;
          initialEditingTextRef.current = '';
        }
        const existing = baseOptions.find(
          (option) => option.label === text || String(option.value) === text,
        );
        const nextValue = existing ? String(existing.value) : text;
        changedValueInCurrentOpenRef.current = nextValue !== '';
        onChange(node.key!, nextValue);
      },
      [baseOptions, cancelPendingEditFocus, effective, node.key, onChange, searchText, value],
    );

    const handleInputKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && searchText.trim()) {
          e.preventDefault();
          e.stopPropagation();
          const trimmed = searchText.trim();
          const existing = baseOptions.find(
            (o) => o.label === trimmed || String(o.value) === trimmed,
          );
          const selectedValue = existing ? String(existing.value) : trimmed;
          startEditingValue(existing ? existing.label : trimmed, true);
          onChange(node.key!, selectedValue);
        }
      },
      [searchText, baseOptions, node.key, onChange, startEditingValue],
    );

    const handleClear = useCallback(() => {
      cancelPendingEditFocus();
      cancelPendingEditBlur();
      cancelPendingClearFocusReset();
      ignoreFocusAfterClearRef.current = true;
      clearFocusResetRafRef.current = window.requestAnimationFrame(() => {
        clearFocusResetRafRef.current = null;
        ignoreFocusAfterClearRef.current = false;
      });
      preserveEditOnNextBlurRef.current = false;
      ignoreSelectionSearchClearRef.current = false;
      editingSelectedValueRef.current = false;
      initialEditingTextRef.current = '';
      setSearchText('');
      onChange(node.key!, '');
    }, [
      cancelPendingClearFocusReset,
      cancelPendingEditBlur,
      cancelPendingEditFocus,
      effective,
      node.key,
      onChange,
      searchText,
      value,
    ]);

    const resetEmptyDropdownActiveOption = useCallback(() => {
      setSelectInstanceKey((prev) => prev + 1);
    }, []);

    const getFallbackText = useCallback(() => {
      if (effective !== undefined && effective !== null && effective !== '') {
        const matched = mergedOptions.find(
          (o) => String(o.value) === String(effective),
        );
        return matched
          ? typeof matched.label === 'string'
            ? matched.label
            : String(matched.label ?? effective)
          : String(effective);
      }
      return placeholder;
    }, [effective, mergedOptions, placeholder]);

    const measureSelectWidth = useCallback(() => {
      const wrapper = wrapperRef.current;
      if (!wrapper) return;
      const selectionEl = wrapper.querySelector<HTMLElement>('.ant-select-selection-item');
      const placeholderEl = wrapper.querySelector<HTMLElement>('.ant-select-selection-placeholder');
      const labelEl = selectionEl || placeholderEl;
      const text = (labelEl?.textContent || '').trim() || getFallbackText();
      const selectorEl = wrapper.querySelector<HTMLElement>('.ant-select-selector');
      const fontHost = labelEl || selectorEl || wrapper;
      const cs = window.getComputedStyle(fontHost);
      const fontSize = labelEl ? cs.fontSize : '16px';
      const fontFamily = cs.fontFamily || 'sans-serif';
      const fontWeight = cs.fontWeight || '400';
      const fontStyle = cs.fontStyle || 'normal';
      const font = `${fontStyle} ${fontWeight} ${fontSize} ${fontFamily}`;
      const canvas = document.createElement('canvas');
      const ctx = navigator.userAgent.includes('jsdom') ? null : canvas.getContext('2d');
      const textWidth = ctx
        ? Math.ceil(((ctx.font = font), ctx.measureText(text).width || 0))
        : Math.ceil(text.length * (Number.parseFloat(fontSize) || 16));
      let padding = 0;
      if (selectorEl) {
        const ss = window.getComputedStyle(selectorEl);
        padding =
          (Number.parseFloat(ss.paddingLeft) || 0) +
          (Number.parseFloat(ss.paddingRight) || 0);
      }
      const arrowEl = wrapper.querySelector<HTMLElement>('.ant-select-arrow');
      const arrowWidth = arrowEl ? Math.ceil(arrowEl.getBoundingClientRect().width) : 12;
      const clearEl = wrapper.querySelector<HTMLElement>('.ant-select-clear');
      let clearWidth = 0;
      if (clearEl) {
        const cs2 = window.getComputedStyle(clearEl);
        if (cs2.display !== 'none' && cs2.visibility !== 'hidden') {
          clearWidth = Math.ceil(clearEl.getBoundingClientRect().width);
        }
      }
      const next = Math.min(240, Math.max(80, textWidth + padding + arrowWidth + clearWidth + 4));
      setWidth((prev) => (prev === next ? prev : next));
    }, [getFallbackText]);

    useLayoutEffect(() => {
      const raf = window.requestAnimationFrame(() => measureSelectWidth());
      const timer = window.setTimeout(() => measureSelectWidth(), 60);
      return () => {
        window.cancelAnimationFrame(raf);
        window.clearTimeout(timer);
      };
    }, [measureSelectWidth]);

    useEffect(() => {
      if (!('fonts' in document)) return;
      let cancelled = false;
      const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
      if (!fonts?.ready) return;
      fonts.ready.then(() => {
        if (!cancelled) measureSelectWidth();
      });
      return () => {
        cancelled = true;
      };
    }, [measureSelectWidth]);

    const hasValue = effective !== undefined && effective !== null && effective !== '';
    // 传给 antd Select 的 value：effective='' 时必须用 undefined。
    // 否则 antd 把空字符串当作"有值"会吞掉 placeholder（用户清空后失焦看不到占位符）。
    const antdValue = effective || undefined;

    return (
      <RequiredMark node={node}>
      <span
        ref={wrapperRef}
        className={[
          'rte-searchable-select-host',
          hasValue ? 'rte-searchable-has-value' : '',
          error ? 'rte-searchable-select-error' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        data-type="searchable-select"
        data-key={node.key}
        data-searching={searchText ? 'true' : 'false'}
        style={{ display: 'inline-block', width }}
      >
        <Select
          key={selectInstanceKey}
          data-type="searchable-select"
          data-key={node.key}
          showSearch
          value={antdValue}
          searchValue={searchText}
          onChange={handleChange}
          onSearch={handleSearch}
          onFocus={() => {
            if (ignoreFocusAfterClearRef.current) return;
            if (hasValue) startEditingValue(getEditableText(String(effective)));
          }}
          onBlur={handleBlur}
          onOpenChange={(open) => {
            if (ignoreFocusAfterClearRef.current) return;
            if (!open) {
              // antd 关闭下拉时可能紧接着触发 onSearch('')，这不是用户删除。
              ignoreSelectionSearchClearRef.current = true;
              if (!hasValue && !changedValueInCurrentOpenRef.current) {
                resetEmptyDropdownActiveOption();
              }
              changedValueInCurrentOpenRef.current = false;
              return;
            }
            ignoreSelectionSearchClearRef.current = false;
            changedValueInCurrentOpenRef.current = false;
            if (hasValue) startEditingValue(getEditableText(String(effective)));
            else {
              cancelPendingEditFocus();
              editingSelectedValueRef.current = false;
              initialEditingTextRef.current = '';
              setSearchText('');
            }
          }}
          onClear={handleClear}
          onInputKeyDown={handleInputKeyDown}
          filterOption={false}
          defaultActiveFirstOption={false}
          options={filteredOptions}
          disabled={disabled}
          placeholder={placeholder}
          style={{ width: '100%' }}
          allowClear={!node.required}
          size="small"
          variant="borderless"
          classNames={{ popup: { root: 'rte-searchable-select-dropdown' } }}
          popupRender={(menu) => (
            <>
              {menu}
              <div className="rte-searchable-select-dropdown-hint">
                可直接在框内输入自定义内容
              </div>
            </>
          )}
          popupMatchSelectWidth={false}
          placement="bottomLeft"
          builtinPlacements={RTE_SELECT_PLACEMENTS}
          getPopupContainer={rteSelectPopupContainer}
          notFoundContent={
            <span style={{ color: '#999', fontSize: 13 }}>暂无数据</span>
          }
          suffixIcon={
            <i
              className="iconfont icon-qiehuan-xiayizhang"
              style={{ fontSize: '10px', color: '#7C3AED' }}
            />
          }
        />
      </span>
      </RequiredMark>
    );
  },
);
SearchableSelectNode.displayName = 'SearchableSelectNode';
